/**
 * Full-frame overlays: title card, countdown, TEMPS ÉCOULÉ slam, pause.
 * Same rules as everything else in this directory — pure functions of (model, age).
 */

import { C, alpha, displayFamily } from './theme.js';
import { drawNum, drawLabel } from './typeface.js';
import { hazard, cone } from './shapes.js';
import { ease, clamp01 } from '../core/mathx.js';
import TUNING from './tuning.js';

const T = TUNING;
const W = T.design.width;
const H = T.design.height;

export function drawTitle(g, m, age) {
  const o = T.overlay;
  const fam = displayFamily();
  g.clearRect(0, 0, W, H);

  const a = ease.outCubic(clamp01(age / o.fade));
  g.save();
  g.globalAlpha = a;

  const sky = g.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, '#101828');
  sky.addColorStop(0.55, '#22242E');
  sky.addColorStop(1, '#0B0D10');
  g.fillStyle = sky;
  g.fillRect(0, 0, W, H);

  hazard(g, 0, 176, W, 26, 44, 0, C.orange, C.ink);
  hazard(g, 0, H - 202, W, 26, 44, 22, C.orange, C.ink);

  const pop = ease.outBack(clamp01((age - 0.12) / 0.5));
  g.save();
  g.translate(W * 0.5, 430);
  g.scale(0.5 + 0.5 * pop, 0.5 + 0.5 * pop);
  g.translate(-W * 0.5, -430);
  drawLabel(g, 'POTHOLE PATROL', W * 0.5, 430, {
    size: o.titleSize, family: fam, tracking: 6, align: 'center', skewDeg: 9,
    fill: C.yellow, outline: 12, outlineColor: C.ink, squeeze: 0.9,
    shadowDx: 12, shadowDy: 14, shadowAlpha: 0.6,
  });
  drawLabel(g, 'MONTRÉAL', W * 0.5, 590, {
    size: o.titleSize * 0.92, family: fam, tracking: 22, align: 'center', skewDeg: 9,
    fill: C.orange, outline: 12, outlineColor: C.ink, squeeze: 0.9,
    shadowDx: 12, shadowDy: 14, shadowAlpha: 0.6,
  });
  g.restore();

  drawLabel(g, 'SERVICE DES TRAVAUX PUBLICS', W * 0.5, 672, {
    size: o.subtitleSize, family: fam, tracking: 8, align: 'center',
    fill: alpha(C.cream, 0.88), outline: 5, outlineColor: C.ink, squeeze: 0.95,
  });

  for (let i = 0; i < 7; i++) {
    cone(g, 150 + i * 106, H - 236, 92 - (i % 3) * 8);
    cone(g, W - 150 - i * 106, H - 236, 92 - ((i + 1) % 3) * 8);
  }

  const blink = 0.55 + 0.45 * Math.sin(age * Math.PI * 2 * 1.1);
  g.globalAlpha = a * blink;
  drawLabel(g, 'APPUYEZ SUR ESPACE', W * 0.5, 790, {
    size: o.promptSize, family: fam, tracking: 8, align: 'center', skewDeg: 7,
    fill: C.white, outline: 6, outlineColor: C.ink, squeeze: 0.95,
  });
  g.restore();
}

export function drawCountdown(g, m, age) {
  const o = T.overlay;
  const fam = displayFamily();
  g.clearRect(0, 0, W, H);

  const n = Math.max(0, Math.ceil(m.countdown));
  const frac = m.countdown - Math.floor(m.countdown);
  const beat = 1 - frac;
  const pop = ease.outBack(clamp01(beat / 0.35));

  g.save();
  g.fillStyle = alpha('#000000', 0.35);
  g.fillRect(0, 0, W, H);

  if (n > 0) {
    drawNum(g, String(n), W * 0.5, H * 0.5 + o.countdownCapH * 0.42, {
      capH: o.countdownCapH, weight: o.countdownWeight, tracking: 0.02, skewDeg: 10,
      align: 'center', fill: C.yellow, fillLow: C.yellowDeep, gloss: alpha(C.yellowHi, 0.9),
      outline: 0.05, outlineColor: C.ink, shadowDx: 12, shadowDy: 14, shadowAlpha: 0.5,
      scale: 0.7 + 0.5 * pop,
    });
    drawLabel(g, 'PRÊT?', W * 0.5, H * 0.5 - o.countdownCapH * 0.72, {
      size: 72, family: fam, tracking: 10, align: 'center', skewDeg: 9,
      fill: C.white, outline: 8, outlineColor: C.ink, squeeze: 0.92,
    });
  } else {
    drawLabel(g, 'ALLEZ!', W * 0.5, H * 0.5 + 60, {
      size: o.goSize, family: fam, tracking: 10, align: 'center', skewDeg: 10,
      fill: C.greenHi, outline: 12, outlineColor: C.ink, squeeze: 0.9,
      shadowDx: 12, shadowDy: 14, shadowAlpha: 0.5,
    });
  }
  g.restore();
}

export function drawTimeUp(g, m, age) {
  const fam = displayFamily();
  g.clearRect(0, 0, W, H);
  const pop = ease.outBack(clamp01(age / 0.4));
  const shake = clamp01(1 - age / 0.6);
  g.save();
  g.fillStyle = alpha('#8A0B04', 0.42 * clamp01(age / 0.25));
  g.fillRect(0, 0, W, H);
  g.translate(W * 0.5 + Math.sin(age * 91) * 22 * shake * shake, H * 0.5);
  g.scale(0.4 + 0.6 * pop, 0.4 + 0.6 * pop);
  drawLabel(g, 'TEMPS ÉCOULÉ', 0, 40, {
    size: 190, family: fam, tracking: 6, align: 'center', skewDeg: 10,
    fill: C.white, outline: 14, outlineColor: C.deepRed, squeeze: 0.9,
    shadowDx: 12, shadowDy: 14, shadowAlpha: 0.55,
  });
  g.restore();
}

export function drawPause(g, m, age) {
  const fam = displayFamily();
  g.clearRect(0, 0, W, H);
  g.save();
  g.fillStyle = alpha('#0B0D10', 0.62);
  g.fillRect(0, 0, W, H);
  hazard(g, 0, H * 0.5 - 132, W, 24, 44, 0, C.orange, C.ink);
  hazard(g, 0, H * 0.5 + 108, W, 24, 44, 22, C.orange, C.ink);
  drawLabel(g, 'PAUSE', W * 0.5, H * 0.5 + 52, {
    size: 176, family: fam, tracking: 18, align: 'center', skewDeg: 9,
    fill: C.yellow, outline: 12, outlineColor: C.ink, squeeze: 0.9,
    shadowDx: 10, shadowDy: 12, shadowAlpha: 0.5,
  });
  drawLabel(g, 'ÉCHAP POUR REPRENDRE', W * 0.5, H * 0.5 + 196, {
    size: 38, family: fam, tracking: 8, align: 'center',
    fill: alpha(C.cream, 0.9), outline: 5, outlineColor: C.ink, squeeze: 0.95,
  });
  g.restore();
}
